import { Icon, innerText, MiddleSpinner, T_MANY, useResponsiveText, useSmallScreen } from '@eagle/react-common';
import { Accordion, AccordionDetails, AccordionSummary, Divider, IconButton, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { FC, PropsWithChildren, ReactNode, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThingEventHeaderProps } from '../thing-detail.types';

interface ThingEventBaseProps extends PropsWithChildren {
  headerContent: ReactNode;
  isFiltering: boolean;
  maxDays: number;
}

interface StyledAccordionSummaryProps {
  smallScreen: boolean;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  padding: 0,
  '& .MuiAccordionSummary-root': { minHeight: 'fit-content', padding: `0 ${theme.spacing(3)}` },
  '& .MuiAccordionSummary-root.Mui-expanded': { minHeight: 'fit-content' },
  '& .MuiAccordionDetails-root': { padding: 0 },
}));

const StyledAccordionSummary = styled(AccordionSummary)<StyledAccordionSummaryProps>(({ smallScreen }) => ({
  '& .MuiAccordionSummary-content': {
    overflow: 'hidden',
    marginRight: !smallScreen ? '48px' : 0, // Compensate for the expandIcon width
  },
}));

export const ThingEventBase: FC<ThingEventBaseProps> = ({ children, headerContent, isFiltering, maxDays }) => {
  const smallScreen = useSmallScreen();
  const [expand, setExpand] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <Suspense fallback={<MiddleSpinner />}>
      <Box ref={scrollRef}>
        <StyledAccordion
          expanded={expand}
          onChange={(_, expanded) => {
            if (!scrollRef || expanded) return;
            scrollRef.current?.scrollIntoView();
          }}
        >
          <StyledAccordionSummary
            smallScreen={smallScreen}
            expandIcon={
              smallScreen ? <></> : (
                <Box sx={{ width: '48px', display: 'flex', justifyContent: 'center' }}>
                  <Icon data-testid="chevron-down-icon" icon="ChevronDown" fontSize="large" style={{ my: 1 }} />
                </Box>
              )
            }
            onClick={() => setExpand(!expand)}
          >
            <Stack
              alignItems={smallScreen ? 'stretch' : 'center'}
              data-testid="events-card-header"
              direction={smallScreen ? 'column' : 'row'}
              spacing={1}
              mb={expand ? (smallScreen ? 2 : 0.5) : 0} justifyContent="space-between" width="100%"
            >
              <ThingEventHeader
                expand={expand}
                isFiltering={isFiltering}
                maxDays={maxDays}
              >
                {headerContent}
              </ThingEventHeader>
            </Stack>
          </StyledAccordionSummary>
          <Divider />
          <AccordionDetails>
            {children}
          </AccordionDetails>
        </StyledAccordion>
      </Box>
    </Suspense>
  );
};

const ThingEventHeader: FC<ThingEventHeaderProps> = ({ expand, isFiltering, children, maxDays }) => {
  const smallScreen = useSmallScreen();
  const { t } = useTranslation(['common', 'track']);
  const theme = useTheme();

  const h5EventText = t('common:terms.event', { count: T_MANY });
  const h5EventStyles = useResponsiveText('1.5rem', innerText(h5EventText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  if (smallScreen) {
    return <>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Stack direction="column" data-testid="events-card-title">
          <Typography color="text.primary" variant="h5" style={{
            fontSize: h5EventStyles.fontSize,
            lineHeight: h5EventStyles.lineHeight,
            wordBreak: h5EventStyles.wordBreak,
            whiteSpace: h5EventStyles.whiteSpace,
            overflow: h5EventStyles.overflow,
            textOverflow: h5EventStyles.textOverflow,
          }}>{h5EventText}</Typography>
          {isFiltering
            && expand
            && <Typography color="GrayText" fontStyle="italic" variant="subtitle2">
              {t('common:component.events-table.hint.result', { count: maxDays })}
            </Typography>
          }
        </Stack>
        <IconButton>
          {expand
            ? <Icon data-testid="chevron-up-icon" icon="ChevronUp" fontSize="large" style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }} />
            : <Icon data-testid="chevron-down-icon" icon="ChevronDown" fontSize="large" style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }} />
          }
        </IconButton>
      </Stack>
      {expand && <>{children}</>}
    </>;
  }

  return <>
    <Stack direction="column" data-testid="events-card-title">
      <Typography color="text.primary" variant="h5" style={{
        fontSize: h5EventStyles.fontSize,
        lineHeight: h5EventStyles.lineHeight,
        wordBreak: h5EventStyles.wordBreak,
        whiteSpace: h5EventStyles.whiteSpace,
        overflow: h5EventStyles.overflow,
        textOverflow: h5EventStyles.textOverflow,
      }}>{h5EventText}</Typography>
      {isFiltering
        && expand
        && <Typography color="GrayText" fontStyle="italic" variant="subtitle2">
          {t('common:component.events-table.hint.result', { count: maxDays })}
        </Typography>
      }
    </Stack>
    <Stack direction="row" flexWrap="wrap" justifyContent="end" data-testid="events-card-filter">
      {expand && <>{children}</>}
    </Stack>
  </>;
};
